//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { BaseClient } from "../../clients/base/BaseClient";
import { LABORATORY_ENDPOINTS } from "./laboratory.consts";
import type { LogoFormat, TermsOfUse } from "./laboratory.types";

//
// ============================================================================
// CLIENT
// ============================================================================
//

/**
 * Laboratory API Client
 *
 * Purpose:
 * Handles laboratory identity and information requests for the LIS Portal.
 *
 * Responsibilities:
 * - Retrieve laboratory logo
 * - Retrieve laboratory name
 * - Retrieve terms of use
 */
export class LaboratoryClient extends BaseClient {
  /**
   * Get laboratory logo (default format)
   *
   * Purpose:
   * Retrieve the laboratory's logo in default format.
   *
   * @param signal - Optional abort signal for request cancellation
   * @returns Logo blob/file
   */
  async getLogo(signal?: AbortSignal | null): Promise<Blob> {
    return this.get<Blob>(LABORATORY_ENDPOINTS.LOGO, { signal });
  }

  /**
   * Get laboratory logo with specific format
   *
   * Purpose:
   * Retrieve the laboratory's logo in specified format (PNG or SVG).
   *
   * @param format - File format (png or svg)
   * @param signal - Optional abort signal for request cancellation
   * @returns Logo blob/file
   */
  async getLogoByFormat(format: LogoFormat, signal?: AbortSignal | null): Promise<Blob> {
    return this.get<Blob>(LABORATORY_ENDPOINTS.LOGO_FORMAT(format), { signal });
  }

  /**
   * Get laboratory name
   *
   * Purpose:
   * Retrieve the laboratory's name, optionally localized.
   *
   * @param culture - Optional language code for localization
   * @param signal - Optional abort signal for request cancellation
   * @returns Laboratory name
   */
  async getName(culture?: string, signal?: AbortSignal | null): Promise<string> {
    return this.get<string>(LABORATORY_ENDPOINTS.NAME, {
      params: culture ? { culture } : undefined,
      signal,
    });
  }

  /**
   * Get terms of use
   *
   * Purpose:
   * Retrieve the laboratory's terms of use, localized by culture.
   *
   * @param culture - Optional language code for localization
   * @param signal - Optional abort signal for request cancellation
   * @returns Terms of use content
   */
  async getTermsOfUse(culture?: string, signal?: AbortSignal | null): Promise<TermsOfUse> {
    return this.get<TermsOfUse>(LABORATORY_ENDPOINTS.TERMS_OF_USE, {
      params: culture ? { culture } : undefined,
      signal,
    });
  }
}
